import GraphemeSplitter from 'grapheme-splitter'
import { fetchFromCDN, fetchMessages, fetchShortcodes } from 'emojibase'
import { createLogger } from './log'
import { getClientMeta } from '@/store/memberships'
import bus from './bus'
import { updateClientMeta } from '@/api/user'
import Fuse from 'fuse.js'
import { supportedReactions } from './constants'
import { isStorageAvailable } from './storage'

// note: in most cases, this does not end up logging anything because
// all of the emoji fetching happens before log capturing is initialized
const log = createLogger('Emojis')

let _shortcodeFuse = null
const isLocalStorageAvailable = isStorageAvailable()

const SHORTCODE_KEY = 'emojibase'
export const EMOJI_VERSION = '17.0.0'

const EMOJIBASE_OPTIONS = {
	version: EMOJI_VERSION,
	local: isLocalStorageAvailable,
	cdnUrl: `${location.origin}/npm/emojibase-data@${EMOJI_VERSION}`,
}

const EMOJI_REGEX = /\p{Emoji}/u
const WHITESPACE_REGEX = /\s/

const RECENT_EMOJIS_MAX = 21
export const QUICK_REACTION_EMOJIS_MAX = 12

const _emojis = []
const _emojiGroups = []
const _emojisByEmoji = new Map()
const _emojisByShortcode = new Map()
const _emojisByGroup = new Map()
const _groupsById = new Map()
const _subgroupsById = new Map()
const _skinsByBaseEmoji = new Map()
let _quickReactionEmojis = []

const isSimpleEmoji = (grapheme) => {
	const chars = Array.from(grapheme)
	if (chars.length === 0) return false
	const codePoint = chars[0].codePointAt(0)
	return EMOJI_REGEX.test(chars[0]) && codePoint > 0x238c
}

const isCombinedIntoEmoji = (grapheme) => {
	const chars = Array.from(grapheme)
	if (chars.length === 0) return false
	return chars.length > 1 && EMOJI_REGEX.test(chars[0])
}

const isEmoji = (grapheme) => {
	return isSimpleEmoji(grapheme) || isCombinedIntoEmoji(grapheme)
}

export const containsOnlyEmoji = (str, max = 100) => {
	const iterator = new GraphemeSplitter().iterateGraphemes(str)
	let count = 0

	while (count <= max) {
		count++
		const { value, done } = iterator.next()
		if (done) return true
		if (WHITESPACE_REGEX.test(value)) continue
		if (!isEmoji(value)) return false
	}
	return false
}

export const clearOldCachedEmojis = () => {
	if (!isLocalStorageAvailable) return
	Object.keys(window.localStorage)
		.filter((key) => key.startsWith('emojibase/'))
		.map((key) => key.split('/'))
		.filter(([x, version]) => version !== EMOJI_VERSION)
		.map((keyParts) => keyParts.join('/'))
		.forEach((key) => {
			log(`clearing old cached emoji data: "${key}"...`)
			window.localStorage.removeItem(key)
		})
}
export const fetchEmojiDataForLocale = async (locale) => {
	log(`fetching emoji data for locale "${locale}"...`)
	// emojibase isn't fully supported in all locales yet, so we use cldr for those 😕
	const shortcodeKey = locale === 'en' ? SHORTCODE_KEY : 'cldr'
	clearOldCachedEmojis()
	const emojis = await fetchFromCDN(`${locale}/data.json`, {
		shortcodes: [shortcodeKey],
		...EMOJIBASE_OPTIONS,
	})
	const shortcodesByHexcode = await fetchShortcodes(
		locale,
		shortcodeKey,
		EMOJIBASE_OPTIONS
	)
	addEmojisToStore(emojis, shortcodesByHexcode)
	const messages = await fetchMessages(locale, EMOJIBASE_OPTIONS)
	addEmojiGroupsToStore(messages.groups)
	addSubgroupsToStore(messages.subgroups)
	bus.$emit('emojis-update')
}

const addEmojiSkinsToStore = (emojiData) => {
	if (!emojiData?.emoji) return null
	const { emoji: baseEmoji, skins = [] } = emojiData
	if (!skins?.length) return
	emojiData.hasSkins = true
	emojiData.baseEmoji = baseEmoji
	delete emojiData.skins
	_skinsByBaseEmoji.set(baseEmoji, skins)
}
const addEmojiGroupsToStore = (groups) => {
	_emojiGroups.length = 0
	groups.forEach((group, i) => {
		group.id = i
		_emojiGroups.push(group)
		_groupsById.set(group.id, group)
	})
}

const addSubgroupsToStore = (subgroups) => {
	_subgroupsById.clear()
	subgroups.forEach((subgroup, i) => {
		subgroup.id = i
		_subgroupsById.set(subgroup.id, subgroup)
	})
}
const addEmojisToStore = (emojis, shortcodesByHexcode = {}) => {
	_emojis.length = 0
	_emojis.push(...emojis)
	_emojisByEmoji.clear()
	_emojisByShortcode.clear()
	emojis.forEach((emojiData) => {
		addShortcodesToEmoji(emojiData, shortcodesByHexcode[emojiData.hexcode])
		addEmojiSkinsToStore(emojiData)
		_emojisByEmoji.set(emojiData.emoji, emojiData)
		addEmojiToGroup(emojiData)
	})
}

const sortEmojisByOrder = (a, b) => {
	return a.order - b.order
}

const addShortcodesToEmoji = (emojiData, shortcodes) => {
	if (!shortcodes?.length) return emojiData
	if (!Array.isArray(shortcodes)) {
		// if there is only one shortcode, it's a string, not an array so we normalize it
		shortcodes = [shortcodes]
	}
	shortcodes.forEach((s) => {
		_emojisByShortcode.set(s, emojiData.emoji)
	})
	emojiData.shortcodes = shortcodes.map((s) => [':', s, ':'].join(''))
	return emojiData
}

const addEmojiToGroup = (emojiData) => {
	const groupId = emojiData.group
	const emojiIds = _emojisByGroup.get(groupId) || []
	emojiIds.push(emojiData.emoji)
	_emojisByGroup.set(groupId, emojiIds)
}

const removeDiacritics = (str) =>
	str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

export const getEmoji = (emojiString) => {
	// Check if the emoji is a skin tone variation
	const baseEmoji = getBaseEmoji(emojiString)
	const emojiData = _emojisByEmoji.get(baseEmoji)
	if (!emojiData) return null

	// If the emoji IS a skin tone variation (e.g. 👊🏻 vs 👊) we should return the emoji data for the skin tone variation
	if (baseEmoji !== emojiString) {
		return {
			...emojiData,
			emoji: emojiString,
			text: emojiString,
			baseEmoji: baseEmoji,
		}
	}

	// Return the emoji data for the base emoji
	return emojiData
}

export const getEmojisByGroup = (groupId) => {
	const emojiStrings = _emojisByGroup.get(groupId) || []
	return emojiStrings
		.map((emojiStr) => _emojisByEmoji.get(emojiStr))
		.sort(sortEmojisByOrder)
}

export const getEmojiFromShortcode = (shortcode) => {
	return _emojisByShortcode.get(shortcode) || null
}

export const getGroupFromEmoji = (emoji) => {
	if (!emoji) return null
	return _groupsById.get(emoji.group) || null
}

export const getSubgroupFromEmoji = (emoji) => {
	if (!emoji) return null
	return _subgroupsById.get(emoji.subgroup) || null
}

export const getAllEmojiGroups = () => {
	return _emojiGroups
		.filter((g) => g.key !== 'component')
		.map((group) => ({
			id: group.id,
			label: group.message,
			key: group.key,
			emojis: getEmojisByGroup(group.id),
		}))
}

export const getSkinsForBaseEmoji = (baseEmoji) => {
	return _skinsByBaseEmoji.get(baseEmoji) || null
}

export const getRecentEmojis = () => {
	const recentEmojis = getClientMeta('recent_emojis') || []
	return recentEmojis.map((emoji) => getEmoji(emoji)).filter(Boolean)
}

export const isQuickReactionEmoji = (emoji) => {
	return _quickReactionEmojis.some((e) => e.emoji === emoji)
}

export const addRecentEmoji = async (emoji) => {
	const recentEmojis = getClientMeta('recent_emojis') || []
	// Early return if the emoji is already at the front so we don't update the client meta for no reason
	if (recentEmojis[0] === emoji) {
		return
	}
	// Remove any existing instances of this emoji
	const dedupedEmojis = recentEmojis.filter((e) => e !== emoji)
	// Add the new emoji to the front and trim the list to max length
	const recent_emojis = [emoji, ...dedupedEmojis].slice(0, RECENT_EMOJIS_MAX)
	await updateClientMeta({ recent_emojis })
	bus.$emit('recent-emojis-updated')
}

export const isEmojiMatch = (emoji, query) => {
	if (!emoji || !query) return false
	const normQuery = removeDiacritics(query.toLowerCase())

	const normLabel = removeDiacritics(emoji.label.toLowerCase())
	const normTags = removeDiacritics(
		(emoji.tags?.join(' | ') || '').toLowerCase()
	)
	const normGroup = removeDiacritics(
		(getGroupFromEmoji(emoji)?.message || '').toLowerCase()
	)
	const normSubgroup = removeDiacritics(
		(getSubgroupFromEmoji(emoji)?.message || '').toLowerCase()
	)

	if (normLabel.includes(normQuery)) return true
	if (normTags.includes(normQuery)) return true
	if (normGroup.includes(normQuery)) return true
	if (normSubgroup.includes(normQuery)) return true
	return false
}

export const searchEmojis = (query, maxResults = 30) => {
	// Split query into words, ignoring whitespace and punctuation and diacritics
	const words = query
		.toLowerCase()
		.split(/[\s\p{P}]+/u)
		.map(removeDiacritics)
		.filter((word) => word.length > 0)

	if (words.length === 0) return []

	// Start with all emojis
	let results = new Set(_emojis)

	// For each word, successively narrow the search
	for (const word of words) {
		const emojiMatches = new Set()

		// Check each emoji in current results
		for (const emoji of results) {
			// Use existing isEmojiMatch function for individual word matching
			if (isEmojiMatch(emoji, word)) {
				emojiMatches.add(emoji)
				continue
			}

			// Special handling for subgroup matches
			const subgroup = getSubgroupFromEmoji(emoji)
			if (subgroup?.message.toLowerCase().startsWith(word)) {
				// If subgroup matches, add all emojis from this subgroup
				const subgroupEmojis = _emojis.filter(
					(e) => e.subgroup === emoji.subgroup
				)
				subgroupEmojis.forEach((e) => emojiMatches.add(e))
			}
		}

		// Narrow our current result set
		results = emojiMatches
	}

	// Convert to array and sort
	return Array.from(results).sort(sortEmojisByOrder).slice(0, maxResults)
}

const initShortcodeFuseIndex = () => {
	_shortcodeFuse = new Fuse(_emojis, {
		threshold: 0.3,
		ignoreDiacritics: true,
		keys: [
			{ name: 'emoticon', weight: 2 },
			{ name: 'shortcodes', weight: 2 },
			{ name: 'tags', weight: 1 },
			{ name: 'label', weight: 1 },
		],
	})
}

export const searchEmojisByShortcode = (searchQueryString, limit = 10) => {
	if (!_shortcodeFuse) {
		initShortcodeFuseIndex()
	}
	return _shortcodeFuse.search(searchQueryString, { limit }).map((r) => ({
		emoji: r.item.emoji,
		label: r.item.label.replaceAll(/flag:/g, '').trim(),
	}))
}

export const findEmojiExactMatch = (query) => {
	return getEmojiFromShortcode(query) || getEmoji(query) || null
}

export const getUserSkinPreference = () => {
	const meta = getClientMeta()
	return meta?.emoji_skin_tone || null
}

export const getPreferredSkinToneForEmoji = (baseEmoji) => {
	if (!baseEmoji) return null
	const skinTone = getUserSkinPreference()
	if (!skinTone) return null
	const skins = getSkinsForBaseEmoji(baseEmoji)
	if (!skins) return null
	return skins.find((s) => s.tone === skinTone) || null
}

export const setUserSkinPreference = (skin) => {
	log('setting user skin preference', skin)
	if (!skin) return

	// Handle multi-tone emojis: only update if all tones are the same
	let toneToSet = skin.tone
	if (Array.isArray(skin.tone)) {
		const uniqueTones = [...new Set(skin.tone)]
		if (uniqueTones.length > 1) {
			// Multiple different tones, don't update preference
			return
		}
		// All tones are the same in array, use that tone
		toneToSet = uniqueTones[0]
	}

	const meta = getClientMeta()
	if (!meta || meta.emoji_skin_tone === toneToSet) return
	if (!toneToSet && meta.emoji_skin_tone) {
		// user is clearing their skin preference
		meta.emoji_skin_tone = null
		return updateClientMeta(meta)
	}
	meta.emoji_skin_tone = toneToSet
	return updateClientMeta(meta)
}

export const getQuickReactionEmojis = () => {
	if (_quickReactionEmojis.length > 0) return _quickReactionEmojis

	_quickReactionEmojis = supportedReactions
		.map(({ native }) => getEmoji(native))
		.filter((x) => x)
		.map((emoji) => ({
			...emoji,
			id: emoji.order,
		}))

	return _quickReactionEmojis
}

export const getBaseEmoji = (emoji) => {
	for (const [baseEmoji, skins] of _skinsByBaseEmoji.entries()) {
		if (skins.some((skin) => skin.emoji === emoji)) {
			return baseEmoji
		}
	}
	return emoji // Return the original emoji if no base emoji is found
}
